import FirstStepTutorial from '../../../app/assets/first-step-tutorial.png';
import SecondStepTutorial from '../../../app/assets/second-step-tutorial.png';
import ThirdStepTutorial from '../../../app/assets/third-step-tutorial.png';
import FourthStepTutorial from '../../../app/assets/fourth-step-tutorial.png';
import FifthStepTutorial from '../../../app/assets/fifth-step-tutorial.png';
import JinnerLogo from '../../../app/assets/logo-white-name-white-without-background-portrait.png';
import { useEffect } from 'react';
import { RootState } from '../../../app/store';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as REQUEST_REQUIREMENTS from '../../../../src/app/api/requestRequirements';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    Link,
    Stack,
    Typography,
    Stepper,
    Step,
    StepLabel,
    StepContent,
} from '@mui/material';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import GroupsIcon from '@mui/icons-material/Groups';
import TuneIcon from '@mui/icons-material/Tune';
import { Email as EmailIcon, Phone as PhoneIcon } from '@mui/icons-material';

const BRAND_RED = '#c0392b';
const BRAND_RED_DARK = '#a32d22';

const BG_MAIN = '#15151a';
const BG_SECTION = '#1c1c24';
const BG_CARD = '#24242e';
const BG_ALT = '#1a1a22';
const BORDER_SUBTLE = '1px solid rgba(255,255,255,0.08)';
const TEXT_PRIMARY = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';

export default function LandingPage() {
    const currentUser = useSelector((state: RootState) => state.session.currentUser);
    const navigate = useNavigate();

    useEffect(() => {
        if (currentUser) {
            try {
                const PRIVATE_ROUTES = REQUEST_REQUIREMENTS.handlePrivateRoutes({ ROUTE_PARAMS: { userNickname: currentUser.nickname } });
                navigate(PRIVATE_ROUTES.USER);
            } catch (e) {
                console.warn('[LandingPage] Erro ao calcular rotas privadas:', e);
            }
        }
    }, []);

    const testimonials = [
        {
            quote: '"I was tired to see a lot of \'beautiful people\' getting a lot of followers on social medias. Where is the music?"',
            name: 'Joseph, Los Angeles - CA - U.S.A',
        },
        {
            quote: '"Oh... I don\'t like to expose myself when I sing. I\'m very shy."',
            name: 'Gisele, Rabat - Morocco',
        },
        {
            quote: '"Music is not so good today, you know?"',
            name: 'Alex - Russia',
        },
    ];

    const features = [
        {
            icon: <MusicNoteIcon />,
            title: 'Collaborate remotely',
            description: 'Record your part anywhere, at your own pace.',
        },
        {
            icon: <HeadphonesIcon />,
            title: 'Audio first',
            description: 'No selfies, no followers race. Just your music.',
        },
        {
            icon: <GroupsIcon />,
            title: 'Build your group',
            description: 'Find musicians who share your genre and vibe.',
        },
        {
            icon: <TuneIcon />,
            title: 'No popularity filters',
            description: "Your track is heard because it's good, not because you're famous.",
        },
    ];

    const tutorialSteps = [
        {
            label: '1. Create a project...',
            description: 'Projects are the canvas for your collaboration. Give it a name, genre, and description.',
            image: FirstStepTutorial,
        },
        {
            label: '2. Go to management session...',
            description: 'The management session is where you organise who contributes what to the project.',
            image: SecondStepTutorial,
        },
        {
            label: '3. Record a content and attach to the project...',
            description: 'Record directly in the app or upload an existing file. Attach it to the project for others to hear.',
            image: ThirdStepTutorial,
        },
        {
            label: '4. Make your content useful...',
            description: "Mark a content as useful so the team knows it's ready to be mixed into the final version.",
            image: FourthStepTutorial,
        },
        {
            label: '5. Mix the project contents selected and merge/close your first version.',
            description: 'Select the useful contents, mix them together, and close the version. Your collaboration is done.',
            image: FifthStepTutorial,
        },
    ];

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                bgcolor: BG_MAIN,
                minHeight: '100vh',
            }}
        >

            {/* Hero */}
            <Box sx={{ bgcolor: BG_MAIN, borderBottom: BORDER_SUBTLE }}>
                <Container maxWidth='lg'>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        alignItems='center'
                        justifyContent='space-between'
                        spacing={{ xs: 6, md: 4 }}
                        sx={{
                            py: { xs: 6, md: 12 },
                            textAlign: { xs: 'center', md: 'left' },
                        }}
                    >

                        {/* LEFT */}
                        <Stack
                            spacing={3}
                            sx={{
                                flex: 1,
                                alignItems: { xs: 'center', md: 'flex-start' },
                            }}
                        >
                            <Typography
                                variant='h1'
                                sx={{
                                    color: TEXT_PRIMARY,
                                    fontWeight: 900,
                                    lineHeight: 1.05,
                                    fontSize: {
                                        xs: '2.3rem',
                                        sm: '3rem',
                                        md: '4.5rem',
                                    },
                                }}
                            >
                                CO-CREATE
                                <br />

                                <Box
                                    component='span'
                                    sx={{
                                        color: BRAND_RED,
                                        textShadow: '0 0 60px rgba(192,57,43,0.4)',
                                    }}
                                >
                                    SONGS
                                </Box>
                            </Typography>

                            <Typography
                                variant='h5'
                                sx={{
                                    color: 'rgba(255,255,255,0.7)',
                                    fontWeight: 400,
                                    fontSize: {
                                        xs: '1.1rem',
                                        md: '1.5rem',
                                    },
                                }}
                            >
                                with other musicians
                            </Typography>

                            <Typography
                                variant='body1'
                                sx={{
                                    color: TEXT_SECONDARY,
                                    maxWidth: 480,
                                    lineHeight: 1.7,
                                    fontSize: {
                                        xs: '0.95rem',
                                        md: '1rem',
                                    },
                                }}
                            >
                                A platform made by amateurs, for amateurs, to amateurs.
                            </Typography>

                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                                sx={{
                                    width: { xs: '100%', sm: 'auto' },

                                    '& .MuiButton-root': {
                                        width: { xs: '100%', sm: 'auto' },
                                    },
                                }}
                            >
                                <Button
                                    variant='contained'
                                    size='large'
                                    sx={{
                                        bgcolor: BRAND_RED,
                                        '&:hover': {
                                            bgcolor: BRAND_RED_DARK,
                                        },
                                        px: 4,
                                        py: 1.5,
                                        fontSize: '1rem',
                                    }}
                                    onClick={() => navigate(REQUEST_REQUIREMENTS.SIGNUP_ENDPOINT)}
                                >
                                    Start creating
                                </Button>

                                <Button
                                    variant='outlined'
                                    size='large'
                                    sx={{
                                        borderColor: 'rgba(255,255,255,0.35)',
                                        color: TEXT_PRIMARY,
                                        px: 4,
                                        py: 1.5,
                                        fontSize: '1rem',

                                        '&:hover': {
                                            borderColor: 'rgba(255,255,255,0.7)',
                                            bgcolor: 'rgba(255,255,255,0.05)',
                                        },
                                    }}
                                    onClick={() => {
                                        document
                                            .getElementById('tutorial-section')
                                            ?.scrollIntoView({ behavior: 'smooth' });
                                    }}
                                >
                                    Watch how it works
                                </Button>
                            </Stack>
                        </Stack>

                        {/* RIGHT LOGO */}
                        <Box
                            sx={{
                                flexShrink: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',

                                width: {
                                    xs: '100%',
                                    md: '42%',
                                    lg: '38%',
                                },

                                mt: {
                                    xs: 2,
                                    md: 0,
                                },
                            }}
                        >
                            <Box
                                component='img'
                                src={JinnerLogo}
                                alt='jinner-logo'
                                sx={{
                                    width: {
                                        xs: '68%',
                                        sm: '52%',
                                        md: '100%',
                                    },

                                    maxWidth: {
                                        xs: 240,
                                        sm: 300,
                                        md: 420,
                                        lg: 500,
                                    },

                                    height: 'auto',
                                    objectFit: 'contain',

                                    filter: 'drop-shadow(0 0 40px rgba(192,57,43,0.18))',
                                }}
                            />
                        </Box>

                    </Stack>
                </Container>
            </Box>

            {/* Features */}
            <Box
                sx={{
                    bgcolor: BG_SECTION,
                    py: { xs: 6, md: 10 },
                    borderBottom: BORDER_SUBTLE,
                }}
            >
                <Container maxWidth='lg'>
                    <Typography
                        variant='h4'
                        sx={{
                            textAlign: 'center',
                            mb: 6,
                            fontWeight: 700,
                            color: TEXT_PRIMARY,
                        }}
                    >
                        Why Jinner?
                    </Typography>

                    <Grid container spacing={3}>
                        {features.map((feature, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        bgcolor: BG_CARD,
                                        border: BORDER_SUBTLE,
                                        borderRadius: '16px',
                                        boxShadow: 'none',
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            textAlign: 'center',
                                            gap: 1.5,
                                            py: 4,
                                        }}
                                    >
                                        <Avatar
                                            sx={{
                                                bgcolor: BRAND_RED,
                                                width: 56,
                                                height: 56,
                                            }}
                                        >
                                            {feature.icon}
                                        </Avatar>

                                        <Typography
                                            variant='h6'
                                            sx={{
                                                fontWeight: 700,
                                                color: TEXT_PRIMARY,
                                            }}
                                        >
                                            {feature.title}
                                        </Typography>

                                        <Typography
                                            variant='body2'
                                            sx={{
                                                color: TEXT_SECONDARY,
                                                lineHeight: 1.7,
                                            }}
                                        >
                                            {feature.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Tutorial */}
            <Box
                id='tutorial-section'
                sx={{
                    bgcolor: BG_SECTION,
                    py: { xs: 6, md: 10 },
                    borderBottom: BORDER_SUBTLE,
                }}
            >
                <Container maxWidth='lg'>
                    <Typography
                        variant='h4'
                        sx={{
                            color: TEXT_PRIMARY,
                            mb: 1,
                            fontWeight: 700,
                        }}
                    >
                        Tutorial
                    </Typography>

                    <Typography
                        variant='h6'
                        sx={{
                            color: TEXT_SECONDARY,
                            mb: 5,
                        }}
                    >
                        Easy as a tool for non-professionals
                    </Typography>

                    <Box sx={{ maxWidth: 900 }}>
                        <Stepper
                            orientation='vertical'
                            nonLinear
                            activeStep={-1}
                            sx={{
                                '& .MuiStepLabel-label': {
                                    color: TEXT_PRIMARY,
                                },

                                '& .MuiStepIcon-root': {
                                    color: BRAND_RED,
                                },

                                '& .MuiStepIcon-text': {
                                    fill: 'white',
                                },

                                '& .MuiStepConnector-line': {
                                    borderColor: 'rgba(255,255,255,0.15)',
                                },
                            }}
                        >
                            {tutorialSteps.map((step, index) => (
                                <Step key={index} expanded>
                                    <StepLabel>{step.label}</StepLabel>

                                    <StepContent>
                                        <Typography
                                            variant='body2'
                                            sx={{
                                                color: TEXT_SECONDARY,
                                                mb: 2,
                                                lineHeight: 1.7,
                                            }}
                                        >
                                            {step.description}
                                        </Typography>

                                        <Box
                                            component='img'
                                            src={step.image}
                                            alt={step.label}
                                            sx={{
                                                width: '50%',
                                                borderRadius: '12px',
                                                border: BORDER_SUBTLE,
                                            }}
                                        />
                                    </StepContent>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                </Container>
            </Box>

            {/* Testimonials */}
            <Box
                sx={{
                    bgcolor: BG_ALT,
                    py: { xs: 6, md: 10 },
                    borderBottom: BORDER_SUBTLE,
                }}
            >
                <Container maxWidth='lg'>
                    <Typography
                        variant='h4'
                        sx={{
                            color: TEXT_PRIMARY,
                            textAlign: 'center',
                            mb: 5,
                            fontWeight: 700,
                        }}
                    >
                        What our community is saying
                    </Typography>

                    <Grid container spacing={3}>
                        {testimonials.map((t, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        bgcolor: '#1e1e1e',
                                        borderLeft: `4px solid ${BRAND_RED}`,
                                        borderRadius: '12px',
                                        boxShadow: 'none',
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            position: 'relative',
                                            py: 3,
                                            px: 3,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                position: 'absolute',
                                                top: 4,
                                                left: 12,
                                                fontSize: {
                                                    xs: '4rem',
                                                    md: '5rem',
                                                },
                                                opacity: 0.12,
                                                color: BRAND_RED,
                                                lineHeight: 1,
                                                fontFamily: 'Georgia, serif',
                                            }}
                                        >
                                            &ldquo;
                                        </Typography>

                                        <Typography
                                            variant='body1'
                                            sx={{
                                                fontStyle: 'italic',
                                                color: TEXT_PRIMARY,
                                                mb: 2,
                                                lineHeight: 1.6,
                                                pt: 2,
                                            }}
                                        >
                                            {t.quote}
                                        </Typography>

                                        <Typography
                                            variant='body2'
                                            sx={{
                                                color: BRAND_RED,
                                                fontWeight: 700,
                                                fontSize: '0.8rem',
                                            }}
                                        >
                                            — {t.name}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* CTA */}
            <Box
                sx={{
                    bgcolor: BG_MAIN,
                    py: { xs: 8, md: 12 },
                    borderBottom: BORDER_SUBTLE,
                }}
            >
                <Container maxWidth='sm' sx={{ textAlign: 'center' }}>
                    <Typography
                        variant='h4'
                        sx={{
                            color: TEXT_PRIMARY,
                            mb: 2,
                            fontWeight: 700,
                        }}
                    >
                        Ready to make music together?
                    </Typography>

                    <Typography
                        variant='body1'
                        sx={{
                            color: TEXT_SECONDARY,
                            mb: 4,
                            lineHeight: 1.7,
                        }}
                    >
                        Join Jinner and start your first collaboration today.
                    </Typography>

                    <Button
                        variant='contained'
                        size='large'
                        sx={{
                            bgcolor: BRAND_RED,

                            '&:hover': {
                                bgcolor: BRAND_RED_DARK,
                            },

                            px: 5,
                            py: 1.75,
                            fontSize: '1rem',
                        }}
                        onClick={() => navigate(REQUEST_REQUIREMENTS.SIGNUP_ENDPOINT)}
                    >
                        Create account
                    </Button>
                </Container>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    bgcolor: BG_SECTION,
                    py: { xs: 4, md: 6 },
                }}
            >
                <Container maxWidth='lg'>
                    <Grid container spacing={4} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                variant='subtitle2'
                                sx={{
                                    color: TEXT_PRIMARY,
                                    fontWeight: 700,
                                    mb: 2,
                                    textTransform: 'uppercase',
                                    letterSpacing: 1,
                                }}
                            >
                                Contact
                            </Typography>

                            <Stack spacing={1.5}>
                                <Stack direction='row' alignItems='center' spacing={1}>
                                    <EmailIcon
                                        sx={{
                                            fontSize: 18,
                                            color: BRAND_RED,
                                        }}
                                    />

                                    <Link
                                        href='mailto:jinner_software@protonmail.com'
                                        sx={{
                                            color: TEXT_SECONDARY,
                                            textDecoration: 'none',

                                            '&:hover': {
                                                color: TEXT_PRIMARY,
                                            },
                                        }}
                                    >
                                        jinner_software@protonmail.com
                                    </Link>
                                </Stack>

                                <Stack direction='row' alignItems='center' spacing={1}>
                                    <PhoneIcon
                                        sx={{
                                            fontSize: 18,
                                            color: BRAND_RED,
                                        }}
                                    />

                                    <Link
                                        href='tel:+5547988002867'
                                        sx={{
                                            color: TEXT_SECONDARY,
                                            textDecoration: 'none',

                                            '&:hover': {
                                                color: TEXT_PRIMARY,
                                            },
                                        }}
                                    >
                                        +55 47 9 8800 2867
                                    </Link>
                                </Stack>
                            </Stack>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography
                                variant='subtitle2'
                                sx={{
                                    color: TEXT_PRIMARY,
                                    fontWeight: 700,
                                    mb: 2,
                                    textTransform: 'uppercase',
                                    letterSpacing: 1,
                                }}
                            >
                                Legal
                            </Typography>

                            <Stack spacing={1.5}>
                                <Link
                                    href='#'
                                    sx={{
                                        color: TEXT_SECONDARY,
                                        textDecoration: 'none',

                                        '&:hover': {
                                            color: TEXT_PRIMARY,
                                        },
                                    }}
                                >
                                    Privacy Policy
                                </Link>

                                <Link
                                    href='#'
                                    sx={{
                                        color: TEXT_SECONDARY,
                                        textDecoration: 'none',

                                        '&:hover': {
                                            color: TEXT_PRIMARY,
                                        },
                                    }}
                                >
                                    Terms of Service
                                </Link>
                            </Stack>
                        </Grid>
                    </Grid>

                    <Box
                        sx={{
                            borderTop: BORDER_SUBTLE,
                            pt: 3,
                            textAlign: 'center',
                        }}
                    >
                        <Typography
                            variant='caption'
                            sx={{
                                color: 'rgba(255,255,255,0.3)',
                            }}
                        >
                            &copy; 2026 Jinner. All rights reserved.
                        </Typography>
                    </Box>
                </Container>
            </Box>

        </Box>
    );
}
